import Keycloak from 'keycloak-js';
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';

import { envInfo, KEYCLOAK_CLIENT_ID, KEYCLOAK_URL } from '@constants/constants';
import { updateAxiosToken } from '@hooks/useAxios';
import { clearUser } from '@utils/openreplay';

type Ctx = { keycloak: Keycloak | null; initialized: boolean };
const KeycloakCtx = createContext<Ctx>({ keycloak: null, initialized: false });

export const useKeycloak = () => useContext(KeycloakCtx);

type KeycloakProviderProps = {
  children: React.ReactNode;
  mock?: boolean;
};

export const KeycloakProvider: React.FC<KeycloakProviderProps> = ({ children, mock }) => {
  const [keycloak, setKeycloak] = useState<Keycloak | null>(null);
  const [initialized, setInitialized] = useState(false);
  const initializingRef = useRef(false);

  const refreshTimer = useRef<number>();

  const clearTimer = () => clearTimeout(refreshTimer.current);

  const scheduleRefresh = (kc: Keycloak) => {
    const exp = (kc.tokenParsed?.exp ?? 0) * 1000; // → ms
    const in50s = 50_000;
    const minWait = 10_000;

    const wait = Math.max(minWait, exp - Date.now() - in50s);

    refreshTimer.current = window.setTimeout(async () => {
      try {
        const refreshed = await kc.updateToken(60); // refresh if <60 s left
        if (refreshed) {
          updateAxiosToken(kc.token);
        }
        if (kc.authenticated) scheduleRefresh(kc); // loop
      } catch (error) {
        console.error('Token refresh failed:', error);
        // Only logout if we're actually authenticated, otherwise just stop the loop
        if (kc.authenticated) {
          clearUser();
          kc.logout();
        }
      }
    }, wait);
  };

  useEffect(() => {
    // Prevent multiple initializations
    if (initializingRef.current || initialized) {
      return;
    }

    initializingRef.current = true;

    if (mock) {
      // Mock Keycloak object
      const mockKeycloak = {
        authenticated: true,
        token: 'mock-token',
        tokenParsed: { exp: Math.floor(Date.now() / 1000) + 3600 },
        updateToken: async () => true,
        logout: () => {},
      } as unknown as Keycloak;
      setKeycloak(mockKeycloak);
      updateAxiosToken('mock-token');
      setInitialized(true);
      initializingRef.current = false;
      return;
    }

    const kc = new Keycloak({
      url: KEYCLOAK_URL,
      realm: envInfo.keycloak_realm,
      clientId: KEYCLOAK_CLIENT_ID,
    });

    kc.init({
      onLoad: 'check-sso',
      pkceMethod: 'S256',
      // Disable silent check SSO in development to prevent flickering
      silentCheckSsoRedirectUri:
        import.meta.env.MODE === 'development' ? undefined : `${window.location.origin}/silent-check-sso.html`,
      // Clean up URL after authentication
      checkLoginIframe: false,
    })
      .then(() => {
        setKeycloak(kc);
        updateAxiosToken(kc.token);

        // Handle authentication success
        if (kc.authenticated) {
          scheduleRefresh(kc);
        }

        setInitialized(true);
        initializingRef.current = false;
      })
      .catch(error => {
        console.warn('Keycloak initialization failed:', error);
        setKeycloak(kc); // Still set keycloak instance even if init fails
        setInitialized(true);
        initializingRef.current = false;
      })
      .finally(() => {
        // Ensure initialization is always marked as complete
        setInitialized(true);
        initializingRef.current = false;
      });

    return clearTimer;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mock]);

  if (!keycloak) {
    return <>{children}</>;
  }

  return <KeycloakCtx.Provider value={{ keycloak, initialized }}>{children}</KeycloakCtx.Provider>;
};
